import { Link } from "react-router-dom";
import { ArrowUpRight, Users, HeartPulse, ShieldCheck, Stethoscope } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";

const PILLARS = [
  {
    icon: Users,
    label: "Patient Accessibility",
    body: "Advocating for regulated access across income groups to ensure medicinal cannabis is a healthcare solution, not a luxury.",
  },
  {
    icon: Stethoscope,
    label: "Priority Medical Groups",
    body: "Supporting evidence-based access for priority patient groups, including palliative care, pediatric epilepsy, and chronic pain management.",
  },
  {
    icon: HeartPulse,
    label: "Health Authority Alignment",
    body: "Working alongside regulators to establish a national patient registry and clinical evidence-generation framework.",
  },
  {
    icon: ShieldCheck,
    label: "Prevention of Harm",
    body: "Implementing rigorous seed-to-shipment traceability to ensure the highest levels of biosecurity and regulatory compliance.",
  },
];

export default function UMCC() {
  return (
    <>
      <section className="pt-[72px] bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <SectionReveal>
            <div className="overline">Policy Leadership · UMCC</div>
          </SectionReveal>
          <SectionReveal delay={120}>
            <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.04] tracking-editorial max-w-5xl">
              Policy Leadership &amp; The UMCC.
            </h1>
          </SectionReveal>
          <SectionReveal delay={240}>
            <p className="mt-8 max-w-2xl text-ink2 text-lg leading-relaxed">
              A patients-first mandate, anchored in domestic public-health responsibility and alignment with Uganda's Ministry of Health.
            </p>
          </SectionReveal>
        </div>
      </section>

      <section className="bg-sand/40 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <SectionReveal className="lg:col-span-5">
              <div className="overline">A Patients-First Mandate</div>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">
                A member of the Uganda Medicinal Cannabis Consortium.
              </h2>
            </SectionReveal>
            <SectionReveal className="lg:col-span-7" delay={140}>
              <div className="space-y-6 text-ink2 text-base leading-relaxed">
                <p>
                  Maisha Pharma is a member of the Uganda Medicinal Cannabis Consortium (UMCC). We believe a successful export industry must be anchored in domestic public-health responsibility and alignment with the Ministry of Health.
                </p>
                <p>
                  Our policy posture is patients-first by design — built around accessibility, evidence, regulatory alignment, and the prevention of harm.
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="overline">Strategic Pillars</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-3xl">
              Four commitments. One public-health mandate.
            </h2>
          </SectionReveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-line/60 border border-line/60">
            {PILLARS.map((p, i) => (
              <SectionReveal key={p.label} delay={i * 90}>
                <div data-testid={`policy-pillar-${i}`} className="group bg-bone p-8 md:p-10 h-full transition-colors hover:bg-sand/30">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 border border-forest/25 flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-bone transition-colors">
                      <p.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[11px] tracking-[0.2em] text-copper">P/0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl tracking-tight">{p.label}</h3>
                  <p className="mt-3 text-sm text-ink2 leading-relaxed">{p.body}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-line/60">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <SectionReveal>
          <div className="overline mb-10">Member Organisations</div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-20">
            <img src="/Images/UMCC Logo.png" alt="UMCC Logo" className="h-20 object-contain" />
            <img src="/Images/GCNC logo.png" alt="GCNC Logo" className="h-20 object-contain" />
            <img src="/Images/Cannabis chamber of commerce logo.png" alt="Cannabis Chamber" className="h-20 object-contain" />
          </div>
        </SectionReveal>
      </div>
    </section>
    <section className="bg-sand/40 border-t border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionReveal>
            <div className="overline">Engage</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-[44px] leading-[1.08] tracking-editorial max-w-2xl">
              For health authorities, clinical partners, and policy stakeholders.
            </h2>
          </SectionReveal>
          <SectionReveal delay={120}>
            <Link to="/contact" data-testid="umcc-cta-contact" className="inline-flex items-center gap-2 bg-forest text-bone px-7 py-4 text-sm tracking-wide hover:bg-forest/85 transition-colors">
              Connect with our policy team <ArrowUpRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
